import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, FileText, Info, Lightbulb, MapPin, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import { PackageCard } from "@/components/Cards";
import { getDestination, useContent } from "@/lib/content";
import { useEnquiry } from "@/components/EnquiryModal";

export default function DestinationDetail() {
  const { slug } = useParams();
  const { destinations, packages, loading } = useContent();
  const { openEnquiry } = useEnquiry();
  const [tab, setTab] = useState("packages");

  const dest = getDestination(destinations, slug);
  const destPackages = useMemo(() => packages.filter((p) => p.destination_slug === slug), [packages, slug]);

  if (loading) return <div className="py-32 text-center text-slate-400">Loading destination...</div>;
  if (!dest) {
    return (
      <div className="py-32 text-center" data-testid="destination-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Destination not found</h1>
        <p className="mt-2 text-slate-500">Try our search or browse all destinations from the menu above.</p>
      </div>
    );
  }

  const tabs = [
    { id: "packages", label: "Popular Packages", icon: Sparkles },
    { id: "todo", label: "Things To Do", icon: MapPin },
    { id: "plan", label: "Plan Your Trip", icon: CalendarClock },
    { id: "tips", label: "Travel Tips", icon: Lightbulb },
    { id: "faqs", label: "FAQs", icon: FileText },
  ];

  return (
    <div data-testid={`destination-detail-${slug}`}>
      <SEO title={`${dest.name} Holidays`} description={dest.intro} />
      <section className="relative h-[52vh] min-h-[420px] flex items-end overflow-hidden grain">
        <img src={dest.image} alt={`${dest.name} — ${dest.tagline}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navydeep/95 via-navy/45 to-navy/20" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-sky-300">{dest.country} · {dest.region}</p>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">{dest.name}</h1>
          <p className="mt-2 font-display italic text-xl text-slate-200">{dest.tagline}</p>
          <button onClick={() => openEnquiry({ destination: dest.name, tripType: dest.region === "India" ? "India Holiday" : "International Holiday" })}
                  data-testid="destination-enquire-button"
                  className="mt-5 rounded-full bg-gold px-7 py-3 font-heading font-bold text-navydeep hover:bg-amber-400 transition-colors">
            Enquire About {dest.name}
          </button>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="max-w-3xl text-lg text-slate-600 leading-relaxed">{dest.intro}</p>
          </Reveal>

          <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200 pb-px" data-testid="destination-tabs">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`destination-tab-${t.id}`}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors ${tab === t.id ? "bg-navy text-white" : "text-slate-600 hover:text-royal hover:bg-sky-50"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "packages" && (
              destPackages.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {destPackages.map((p) => <Reveal key={p.slug}><PackageCard pkg={p} /></Reveal>)}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="destination-custom-cta">
                  <p className="text-slate-600">Ready-made packages for {dest.name} are being finalised — but we craft fully customised {dest.name} holidays every week.</p>
                  <button onClick={() => openEnquiry({ destination: dest.name, tripType: dest.region === "India" ? "India Holiday" : "International Holiday" })}
                          className="mt-4 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-royal transition-colors">
                    Request a Custom {dest.name} Quote
                  </button>
                </div>
              )
            )}

            {tab === "todo" && (
              <div className="grid sm:grid-cols-2 gap-4">
                {(dest.things_to_do || []).map((t, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <div className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm">
                      <span className="font-display italic text-2xl text-gold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-sm text-slate-700 leading-relaxed">{t}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}

            {tab === "plan" && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                  <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-skybrand" /> Best Time to Visit</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{dest.best_time}</p>
                </div>
                <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                  <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Info className="w-5 h-5 text-skybrand" /> Visa Information</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{dest.visa_info}</p>
                  <p className="mt-3 text-xs text-slate-400">Visa approval is subject to the respective embassy / immigration authority.</p>
                </div>
              </div>
            )}

            {tab === "tips" && (
              <ul className="space-y-3">
                {(dest.tips || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm text-sm text-slate-700">
                    <Lightbulb className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            )}

            {tab === "faqs" && <div className="max-w-3xl"><FaqAccordion faqs={dest.faqs || []} testidPrefix="destination-faq" /></div>}
          </div>
        </div>
      </section>
    </div>
  );
}
